<template>
  <b-container class="py-4">
    <b-row class="justify-content-center">
      <b-col lg="8">
        <b-alert
          v-if="status === 'NOTIDENTIFIED'"
          show
          variant="warning"
          class="onboarding-banner mb-4"
        >
          <b-icon icon="shield-exclamation" class="mr-2" />
          Уважаемый Клиент! Вам необходимо пройти верификацию личности для осуществления торговых операций на платформе.
        </b-alert>

        <!-- ===== STATUS CARD ===== -->
        <div class="status-card">
          <!-- NOT IDENTIFIED — выбор способа и запуск онбординга.
               Раньше здесь была кнопка-заглушка без method picker, из-за
               чего новый клиент застревал: выбрать метод было негде. -->
          <div v-if="status === 'NOTIDENTIFIED'" class="status-block pending">
            <h4>Процесс онбординга</h4>
            <p class="mb-2">Выберите способ идентификации.</p>

            <!-- Method picker. Hidden once the AMLBot session is open so we
                 don't tempt the user to switch mid-flow (a new "Новая
                 ссылка" button takes them back). -->
            <div v-if="!kycFormUrl" class="kyc-method-grid mb-3">
              <button
                type="button"
                class="kyc-method-card"
                :class="{ 'is-selected': kycMethod === 'IIN' }"
                @click="kycMethod = 'IIN'"
              >
                <b-icon icon="person-badge" class="kyc-method-icon" />
                <div class="kyc-method-title">По ИИН</div>
                <div class="kyc-method-sub">
                  Быстро. Подходит резидентам Казахстана.
                </div>
              </button>
              <button
                type="button"
                class="kyc-method-card"
                :class="{ 'is-selected': kycMethod === 'DOCUMENT' }"
                @click="kycMethod = 'DOCUMENT'"
              >
                <b-icon icon="card-image" class="kyc-method-icon" />
                <div class="kyc-method-title">По документу</div>
                <div class="kyc-method-sub">
                  Паспорт или удостоверение + селфи. Для всех.
                </div>
              </button>
            </div>

            <div class="pending-actions">
              <b-button
                variant="warning"
                pill
                class="start-btn"
                :disabled="creatingKyc || (!kycFormUrl && !kycMethod)"
                @click="startOnboarding(false)"
              >
                <b-icon icon="shield-check" class="mr-2" />
                {{ creatingKyc ? 'Создаём сессию…' : 'Начать онбординг' }}
              </b-button>

              <div v-if="creatingKyc" class="mt-3">
                <b-spinner small />
                <span class="ml-2 text-muted-soft">Подготовка формы AMLBot…</span>
              </div>

              <!-- ====== IFRAME / FALLBACK ====== -->
              <div v-if="kycFormUrl" class="iframe-shell mt-4">
                <div class="iframe-header">
                  <div class="iframe-title">
                    <b-icon icon="person-check" class="mr-2" />
                    Идентификация AMLBot
                  </div>

                  <div class="iframe-meta">
                    <span v-if="kycVerificationId" class="meta-pill">
                      ID: {{ kycVerificationId }}
                    </span>

                    <b-button size="sm" variant="outline-light" class="ml-2" @click="openInNewTab">
                      <b-icon icon="box-arrow-up-right" class="mr-1" />
                      Открыть
                    </b-button>

                    <b-button size="sm" variant="outline-light" class="ml-2" @click="copyText(kycFormUrl)">
                      <b-icon icon="clipboard" class="mr-1" />
                      Копировать
                    </b-button>

                    <b-button
                      size="sm"
                      variant="outline-light"
                      class="ml-2"
                      :disabled="creatingKyc"
                      @click="startOnboarding(true)"
                    >
                      <b-icon icon="arrow-repeat" class="mr-1" />
                      Новая ссылка
                    </b-button>
                  </div>
                </div>

                <!-- WebGL fallback -->
                <div v-if="iframeProblem" class="iframe-problem">
                  <div class="problem-title">
                    <b-icon icon="gpu-card" class="mr-2" />
                    Не удалось открыть камеру внутри встроенного окна
                  </div>

                  <div class="problem-text">
                    Иногда браузер блокирует WebGL/ускорение для видео в iFrame.
                    Решение: откройте онбординг в новой вкладке.
                  </div>

                  <div class="problem-actions">
                    <b-button variant="light" pill @click="openInNewTab">
                      <b-icon icon="box-arrow-up-right" class="mr-2" />
                      Открыть в новой вкладке
                    </b-button>

                    <b-button variant="outline-light" pill class="ml-2" @click="showHelp = !showHelp">
                      <b-icon icon="question-circle" class="mr-2" />
                      {{ showHelp ? 'Скрыть подсказку' : 'Показать подсказку' }}
                    </b-button>
                  </div>

                  <div v-if="showHelp" class="help-list">
                    <ul class="mb-0">
                      <li>Включите <b>аппаратное ускорение</b> в браузере и перезапустите.</li>
                      <li>Откройте ссылку в Chrome/Edge (последняя версия).</li>
                      <li>Отключите AdBlock/Privacy расширения на время онбординга.</li>
                      <li>Если вы в RDP/VM — попробуйте на обычном ПК/ноутбуке.</li>
                    </ul>
                  </div>
                </div>

                <!-- Iframe itself -->
                <div v-else class="iframe-wrap">
                  <div class="iframe-top-hint">
                    <span class="hint-pill">
                      Если появилось сообщение про WebGL/камеру —
                      <a href="javascript:void(0)" @click="iframeProblem = true">нажмите сюда</a>
                    </span>
                  </div>

                  <iframe
                    :src="kycFormUrl"
                    title="AMLBot KYC"
                    allow="camera; microphone; autoplay; fullscreen; clipboard-read; clipboard-write"
                    allowfullscreen
                    referrerpolicy="no-referrer"
                  ></iframe>
                </div>

                <div class="iframe-footer">
                  <b-icon icon="info-circle" class="mr-2" />
                  Ссылка одноразовая. Если закрыли страницу — нажмите «Новая ссылка».
                </div>
              </div>

              <!-- ===== Polling status ===== -->
              <div v-if="kycVerificationId" class="mt-3 w-100" style="max-width: 980px;">
                <div v-if="polling && !kycCompleted" class="polling-pill">
                  <b-spinner small />
                  <span class="ml-2">
                    Идёт проверка. Пожалуйста, подождите до завершения.
                  </span>
                </div>

              </div>

              <!-- ===== Completed result ===== -->
              <div
                v-if="kycCompleted && kycResult"
                ref="kycResultBlock"
                class="mt-4 w-100"
                style="max-width: 980px;"
              >
                <div v-if="kycPassed" class="status-block approved">
                  <h4>Идентификация пройдена ✅</h4>
                  <p class="mb-0">Проверка завершена. Вы можете продолжать работу.</p>

                  <b-button variant="success" pill class="mt-3" @click="onKycSuccessContinue">
                    Продолжить
                  </b-button>
                </div>

                <div v-else class="status-block rejected">
                  <h4>Идентификация не пройдена ❌</h4>
                  <p class="mb-0">Проверка завершена, но есть замечания. Посмотрите детали ниже.</p>

                  <b-button variant="warning" pill class="mt-3" @click="restartKyc">
                    Пройти KYC заново
                  </b-button>
                </div>

                <div class="details-box mt-3" v-if="kycResult.verifications">
                  <h5>Детали проверок</h5>

                  <div class="detail-item" v-for="(v, key) in kycResult.verifications" :key="key">
                    <div class="detail-head">
                      <span class="detail-key">{{ formatKey(key) }}</span>
                      <span class="detail-badge" :class="{ ok: v.verified, fail: !v.verified }">
                        {{ v.verified ? '✅ OK' : '❌ FAIL' }}
                      </span>
                    </div>

                    <div class="detail-body">
                      <div v-if="v.comment"><b>Комментарий:</b> {{ v.comment }}</div>

                      <div v-if="v.decline_reasons && v.decline_reasons.length">
                        <b>Причины:</b> {{ v.decline_reasons.join(', ') }}
                      </div>

                      <div
                        v-if="(!v.comment) && (!v.decline_reasons || !v.decline_reasons.length)"
                        class="text-muted-soft"
                      >
                        Замечаний нет.
                      </div>
                    </div>
                  </div>
                </div>

                <div class="mt-3 text-muted-soft" v-if="kycResult.applicant_id">
                  Applicant ID: <b>{{ kycResult.applicant_id }}</b>
                </div>

                <div class="mt-1 text-muted-soft" v-if="kycResult.verification_id">
                  Verification ID: <b>{{ kycResult.verification_id }}</b>
                </div>
              </div>
            </div>
          </div>

          <!-- PENDING — заявка на идентификацию отправлена и проходит
               проверку. Онбординг здесь не начинают заново. -->
          <div v-else-if="status === 'PENDING'" class="status-block pending">
            <h4>Идентификация на проверке</h4>
            <p class="mb-0">Ваши данные приняты и проходят проверку. Мы уведомим вас о результате.</p>
          </div>

          <!-- APPROVED: survey step (once, before test) -->
          <div v-else-if="status === 'APPROVED' && !surveyCompleted" class="status-block approved survey-block">
            <p class="approved-badge">Онбординг подтверждён</p>
            <h4 class="approved-title">{{ (surveyDefinition && surveyDefinition.titleRu) || 'Анкета' }}</h4>
            <p class="approved-desc mb-3">Пожалуйста, заполните анкету. Она показывается один раз.</p>

            <div v-if="surveyDefinitionLoading" class="text-center my-4">
              <b-spinner small /> <span class="ml-2 text-muted">Загрузка опросника…</span>
            </div>
            <div v-else-if="surveyDefinitionError" class="text-center my-3">
              <span class="text-danger">{{ surveyDefinitionError }}</span>
              <div class="mt-2">
                <b-button size="sm" variant="outline-secondary" @click="loadSurveyDefinition">Повторить</b-button>
              </div>
            </div>
            <b-form v-else class="survey-form">
              <div
                v-for="q in surveyChoiceQuestions"
                :key="q.questionId"
                class="survey-field mb-3"
              >
                <label class="survey-label">{{ q.titleRu || q.code }}</label>
                <b-form-select
                  :value="surveyAnswers[q.code] || null"
                  @change="v => $set(surveyAnswers, q.code, v)"
                  :options="optionsForQuestion(q)"
                  value-field="value"
                  text-field="text"
                  class="survey-select"
                />
              </div>
              <!-- Phone is identity (lms_core_user.phonenumber), not a
                   survey question. Rendered alongside since we still
                   collect it on the same onboarding screen. -->
              <div class="survey-field mb-3">
                <label class="survey-label">Номер телефона</label>
                <b-form-input
                  :value="surveyPhoneNumber"
                  @input="onSurveyPhoneInput"
                  type="tel"
                  placeholder="+7 (___) ___-__-__"
                  class="survey-select"
                  :state="surveyPhoneValid"
                  inputmode="numeric"
                  maxlength="18"
                />
                <small v-if="surveyPhoneValid === false" class="text-danger">
                  Введите номер в формате +7 (XXX) XXX-XX-XX.
                </small>
              </div>
              <b-button
                variant="success"
                pill
                class="approved-cta mt-2"
                :disabled="!surveyCanSubmit() || surveySubmitting"
                @click="submitSurvey"
              >
                {{ surveySubmitting ? 'Отправка…' : 'Отправить' }}
              </b-button>
            </b-form>
          </div>

          <!-- APPROVED: test step (after survey done) -->
          <div v-else-if="status === 'APPROVED'" class="status-block approved approved-next-step">
            <p class="approved-badge">Онбординг подтверждён</p>
            <p class="approved-desc mt-2">
              Ваш текущий лимит инвестирования — <strong>1 000 USD</strong> в месяц.
            </p>
            <p class="approved-desc" v-if="basicTestResult =='failed'">
              Короткий тест на понимание рисков — это требование для вашей безопасности. После него вы сможете создавать заказы без ограничений.
            </p>
            <b-button v-if="basicTestResult == 'failed'" variant="success" pill class="approved-cta mt-3" @click="$refs.basicTestModal.show()">
              Пройти тест →
            </b-button>
            <b-button v-else-if="basicTestResult=='passed'" variant="success" pill class="approved-cta mt-3" @click="$router.push('/landing')">
              Вернуться на главную
            </b-button>
          </div>

          <!-- REJECTED -->
          <div v-else-if="status === 'REJECTED'" class="status-block rejected">
            <h4>Онбординг отклонён</h4>
            <p>Ознакомьтесь с комментариями администратора и отправьте исправления.</p>
          </div>
        </div>

        <!-- ===== ADMIN COMMENTS ===== -->
        <div v-if="status === 'REJECTED' && hasComments" class="comments-box">
          <h5>Комментарии администратора</h5>

          <div v-for="c in loadedUserData.actionComments" :key="c.commentId" class="comment-item">
            <div class="comment-header">
              <span>{{ c.commentedBy || 'Администратор' }}</span>
              <span>{{ c.commentedUserRole }}</span>
              <span>{{ formatDate(c.createdDate) }}</span>
            </div>

            <div class="comment-body">
              {{ c.actionComment }}
            </div>
          </div>
        </div>

        <!-- ===== USER REPLY ===== -->
        <div v-if="status === 'REJECTED'" class="reply-box">
          <h5>Ответить на комментарии</h5>

          <b-form-textarea
            v-model="replyText"
            rows="4"
            placeholder="Введите ваш комментарий…"
            class="mb-3"
          />

          <b-form-file
            v-model="replyFile"
            browse-text="Выбрать файл"
            placeholder="Файл не выбран"
            class="mb-3"
          />

          <b-button variant="success" pill :disabled="!replyText" @click="sendReply">
            Отправить ответ
          </b-button>
        </div>
      </b-col>
    </b-row>

    <BasicTestModal ref="basicTestModal" @passed-close="onBasicTestPassedClose" />
  </b-container>
</template>

<script>
import axios from 'axios'
import { apiDomain, getHeader } from '@/config/config'
import { mapState } from 'vuex'
import BasicTestModal from '@/components/landing/BasicTestModal.vue'

export default {
  name: 'KycStatusCard',

  components: { BasicTestModal },

  data() {
    return {
      basicTestResult: "failed",
      operationType: 'BUY',

      loadedUserData: {},
      replyText: '',
      replyFile: null,

      creatingKyc: false,
      kycFormUrl: '',
      kycVerificationId: '',
      // 'IIN' | 'DOCUMENT'. Empty until the user picks an option; the
      // start button stays disabled until then. Backend selects the
      // matching AMLBot form id based on this value.
      kycMethod: '',

      iframeProblem: false,
      showHelp: false,

      pollTimer: null,
      polling: false,
      pollIntervalMs: 5000,
      pollStartedAt: null,
      pollMaxMinutes: 20,

      kycResult: null,
      kycCompleted: false,
      kycPassed: null,

      // Survey is now driven by the backend catalog. surveyDefinition
      // holds the active version's questions + options; surveyAnswers
      // stores user picks keyed by question.code so the POST payload
      // can map back to the legacy {sourceOfFunds,experience,plannedVolume}
      // field names without a wire-format change.
      surveyDefinition: null,
      surveyDefinitionLoading: false,
      surveyDefinitionError: null,
      surveyAnswers: {},
      surveyPhoneNumber: '',
      surveySubmitting: false,

      surveyCompleted: false
    }
  },

  computed: {
    ...mapState(['loginedUser']),

    status() {
      return this.loadedUserData.onboardingStatus || 'NOTIDENTIFIED'
    },

    hasComments() {
      return (
        this.loadedUserData.actionComments &&
        this.loadedUserData.actionComments.length > 0
      )
    },

    // Strict KZ format: full mask "+7 (XXX) XXX-XX-XX" -> 11 digits with
    // a leading 7. Null while empty so the input doesn't paint red on a
    // fresh form.
    surveyPhoneValid() {
      const raw = (this.surveyPhoneNumber || '').trim()
      if (!raw) return null
      const digits = raw.replace(/\D+/g, '')
      return digits.length === 11 && digits.startsWith('7')
    },
    // Normalised E.164-ish value sent to the backend: "+7XXXXXXXXXX".
    surveyPhoneE164() {
      const digits = (this.surveyPhoneNumber || '').replace(/\D+/g, '')
      return digits.length === 11 ? '+' + digits : ''
    },
    surveyChoiceQuestions() {
      const qs = (this.surveyDefinition && this.surveyDefinition.questions) || []
      return qs
        .filter(q => q.qtype === 'SELECT')
        .slice()
        .sort((a, b) => (a.position || 0) - (b.position || 0))
    }
  },

  watch: {
    '$route.params.type'(newVal) {
      this.setOperationTypeFromRoute(newVal)
      // optional: reset UI if type changes
      // this.resetKycUi()
    }
  },
     
  methods: {
    /**
     * Map API/Axios failures to short copy; errors are logged to the console only (no in-page alerts).
     */
    friendlyApiError(err, fallbackRu) {
      const fallback = fallbackRu || 'Произошла ошибка. Попробуйте позже.'
      const status = err && err.response && err.response.status
      const data = err && err.response && err.response.data

      if (data != null && typeof data === 'object' && !Array.isArray(data)) {
        const m = data.message || data.msg || data.error
        if (typeof m === 'string' && m.trim()) return m.trim()
      }
      if (typeof data === 'string') {
        const t = data.trim()
        if (t && !t.startsWith('<') && t.length < 400) return t
      }

      const axMsg = err && err.message
      if (
        typeof axMsg === 'string' &&
        /Request failed with status code|Network Error|ECONNABORTED|timeout/i.test(axMsg)
      ) {
        if (status >= 500) return 'Сервер временно недоступен. Попробуйте позже.'
        if (status === 401 || status === 403) return 'Сессия истекла. Войдите в систему снова.'
        if (status === 404) return 'Данные не найдены. Обновите страницу или обратитесь в поддержку.'
        return fallback
      }
      if (typeof axMsg === 'string' && axMsg.trim() && axMsg.length < 240) {
        return axMsg.trim()
      }
      return fallback
    },

    logIdentificationIssue(context, err, fallbackRu) {
      const msg = this.friendlyApiError(err, fallbackRu)
      console.error(`[Identification:${context}]`, msg, err || '')
    },

    async setOperationTypeFromRoute(type) {
      await this.fetchUserDetails();
      await this.fetchLastKycResult();
      await this.fetchBasicTestResult();

      const up = String(type || 'SHOW').toUpperCase();
      this.operationType = up;
      let onboardingStatus = String(
        this.loadedUserData.onboardingStatus || 'NOTIDENTIFIED'
      );
      // Redirect to BUY/SELL only if KYC approved AND basic test passed
      if (
        (this.operationType === 'SELL' || this.operationType === 'BUY') &&
        onboardingStatus === 'APPROVED' &&
        this.basicTestResult === 'passed'
      ) {
        this.$router.replace(`/buy/${this.operationType}`);
        return
      }
      // Nudge the user to the basic risk test if they've finished KYC
      // + the onboarding survey but haven't passed the basic test yet.
      // Without this the page reads as if onboarding is "done", and
      // the "Пройти тест →" button gets lost below the fold.
      if (
        onboardingStatus === 'APPROVED' &&
        !!this.loadedUserData.isSurveyShown &&
        this.basicTestResult !== 'passed'
      ) {
        this.notifyTakeBasicTest()
      }
    },

    notifyTakeBasicTest() {
      // One toast per page-mount — store the flag on data so a watcher
      // / route change can reset it later if we ever need to retoast.
      if (this._basicToastShown) return
      this._basicToastShown = true
      const h = this.$createElement
      const title = h('div', { class: 'd-flex align-items-center' }, [
        h('b-icon', { props: { icon: 'shield-exclamation' }, class: 'mr-2' }),
        h('strong', 'Остался последний шаг')
      ])
      this.$bvToast.toast('Чтобы продолжить, пройдите короткий тест на понимание рисков.', {
        title,
        toaster: 'b-toaster-top-center',
        variant: 'warning',
        solid: true,
        autoHideDelay: 5500,
        appendToast: true
      })
    },

    fetchBasicTestResult() {
      return axios
        .get(`${apiDomain}/ptest/result/BASIC`, { headers: getHeader() })
        .then(res => {
          this.basicTestResult = res.data;
        })
        .catch(err => {
          this.basicTestResult = 0;
        });
    },

    optionsForQuestion(q) {
      const opts = [{ value: null, text: 'Выберите…' }]
      const src = (q && q.options) || []
      for (const o of src) {
        opts.push({ value: o.code, text: o.titleRu || o.code })
      }
      return opts
    },
    loadSurveyDefinition() {
      this.surveyDefinitionLoading = true
      this.surveyDefinitionError = null
      axios
        .get(`${apiDomain}/ptest/survey/ONBOARDING`, { headers: getHeader() })
        .then(res => {
          this.surveyDefinition = res.data || null
        })
        .catch(err => {
          console.error('survey load failed:', err)
          this.surveyDefinitionError = 'Не удалось загрузить опросник.'
        })
        .finally(() => {
          this.surveyDefinitionLoading = false
        })
    },
    submitSurvey() {
      if (!this.surveyCanSubmit() || this.surveySubmitting) return;
      this.surveySubmitting = true
      const url = `${apiDomain}/ptest/survey`
      // Wire format stays compatible — keys match the SurveyRequest
      // struct on the backend, and SurveyAnswerInput maps them by code.
      const body = {
        sourceOfFunds: this.surveyAnswers.source_of_funds,
        experience: this.surveyAnswers.experience,
        plannedVolume: this.surveyAnswers.planned_volume,
        phoneNumber: this.surveyPhoneE164
      }
      axios
        .post(url, body, { headers: getHeader() })
        .then(() => {
          return this.fetchUserDetails()
        })
        .then(() => {
          this.surveySubmitting = false
          this.surveyCompleted = true
          if (!this.loadedUserData.isSurveyShown) {
            this.$set(this.loadedUserData, 'isSurveyShown', true)
          }
        })
        .catch(err => {
          this.surveySubmitting = false
          this.logIdentificationIssue(
            'survey',
            err,
            'Не удалось отправить анкету. Попробуйте позже.'
          )
        })
    },

    fetchUserDetails() {
      return axios
        .get(`${apiDomain}/exchange/userdetails/${this.loginedUser.id}`, {
          headers: getHeader()
        })
        .then(res => {
          this.loadedUserData = res.data;
          this.surveyCompleted = !!this.loadedUserData.isSurveyShown;
        })
        .catch(err => console.error(err));
    },

    surveyCanSubmit() {
      const allRequiredAnswered = (this.surveyChoiceQuestions || []).every(q => {
        if (!q.isRequired) return true
        const v = this.surveyAnswers[q.code]
        return v != null && v !== ''
      })
      return allRequiredAnswered && this.surveyPhoneValid === true
    },
    onSurveyPhoneInput(value) {
      // Normalise any user input to the "+7 (XXX) XXX-XX-XX" template:
      // - keep only digits
      // - treat a leading 8 as a typo for 7 (common KZ habit)
      // - force first digit to 7 even when the user types the area code
      //   without the country prefix
      // - format incrementally as the user keeps typing so the cursor
      //   doesn't fight a fully-pre-rendered mask
      let digits = String(value || '').replace(/\D+/g, '')
      if (digits.startsWith('8')) digits = '7' + digits.slice(1)
      if (digits && !digits.startsWith('7')) digits = '7' + digits
      digits = digits.slice(0, 11)
      this.surveyPhoneNumber = this.formatKzPhone(digits)
    },
    formatKzPhone(digits) {
      if (!digits) return ''
      const tail = digits.slice(1) // 10 digits after the leading 7
      let out = '+7'
      if (tail.length > 0) out += ' (' + tail.slice(0, 3)
      if (tail.length >= 3) out += ')'
      if (tail.length > 3) out += ' ' + tail.slice(3, 6)
      if (tail.length > 6) out += '-' + tail.slice(6, 8)
      if (tail.length > 8) out += '-' + tail.slice(8, 10)
      return out
    },
    fetchLastKycResult() {
      return axios
        .get(`${apiDomain}/exchange/kyclastresult`, { headers: getHeader() })
        .then(res => {
          const d = res.data || {}
          // If API returned empty object -> ignore
          if (!d || Object.keys(d).length === 0) return

          this.kycResult = d

          // try to get verification id from response (your JSON shows verification_id)
          const vid =
            d.verification_id ||
            d.verificationId ||
            this.kycVerificationId ||
            ''
          
          if (vid) this.kycVerificationId = vid

          const st = String(d.status || '').toLowerCase()
          if (st === 'completed') {
            this.kycCompleted = true
            this.kycPassed = !!d.verified
            this.stopPollingKyc()
            this.scrollToKycResult()
          }
          // Don't auto-start polling on page load — polling only starts
          // when the user actively begins an onboarding session via startOnboarding()
        })
        .catch(() => {
          //alert("no data");
        })
    },

    resetKycUi() {
      this.iframeProblem = false
      this.showHelp = false

      this.stopPollingKyc()

      this.creatingKyc = false
      this.kycFormUrl = ''
      this.kycVerificationId = ''

      this.polling = false
      this.pollStartedAt = null

      this.kycResult = null
      this.kycCompleted = false
      this.kycPassed = null
    },

    scrollToKycResult() {
      this.$nextTick(() => {
        const el = this.$refs.kycResultBlock
        if (el && el.scrollIntoView) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      })
    },

    // ✅ NEW: load last KYC result on page open
    

    

    startOnboarding(refresh = false) {
      if (this.creatingKyc) return

      if (refresh) this.resetKycUi()
      if (!refresh && this.kycFormUrl) return

      this.creatingKyc = true

      axios
        .post(
          `${apiDomain}/exchange/kyc/create`,
          { userId: this.loginedUser.id, method: this.kycMethod || 'DOCUMENT' },
          { headers: getHeader() }
        )
        .then(res => {
          const d = res.data || {}

          const url =
            d.form_url ||
            d.formUrl ||
            d.url ||
            d.kyc_url ||
            d.kycUrl ||
            ''

          const vid =
            d.verificationID ||
            d.verification_id ||
            d.verificationId ||
            ''

          if (!url || !vid) {
            console.error(
              '[Identification:kyc/create]',
              'Ссылка или verificationID не получены. Ожидаем поля form_url и verificationID.',
              d
            )
            return
          }

          this.kycFormUrl = url
          this.kycVerificationId = vid

          this.startPollingKyc()
        })
        .catch(err => {
          this.logIdentificationIssue(
            'kyc/create',
            err,
            'Не удалось начать проверку личности. Попробуйте позже.'
          )
        })
        .finally(() => {
          this.creatingKyc = false
        })
    },

    startPollingKyc() {
      if (!this.kycVerificationId) return

      if (this.pollTimer) clearInterval(this.pollTimer)

      this.polling = true
      this.pollStartedAt = Date.now()

      this.fetchKycStatus()

      this.pollTimer = setInterval(() => {
        const elapsedMin = (Date.now() - this.pollStartedAt) / 60000
        if (elapsedMin > this.pollMaxMinutes) {
          console.warn(
            '[Identification:kyc/poll]',
            `Проверка занимает дольше обычного. Попробуйте позже (таймаут ${this.pollMaxMinutes} мин).`
          )
          this.stopPollingKyc()
          return
        }
        this.fetchKycStatus()
      }, this.pollIntervalMs)
    },

    stopPollingKyc() {
      this.polling = false
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
    },

    fetchKycStatus() {
      if (!this.kycVerificationId) return

      axios
        .get(`${apiDomain}/exchange/kyc/status/${this.kycVerificationId}`, {
          headers: getHeader()
        })
        .then(res => {
          const d = res.data || {}
          this.kycResult = d

          const st = String(d.status || '').toLowerCase()
          if (st === 'completed') {
            this.kycCompleted = true
            this.kycPassed = !!d.verified
            this.stopPollingKyc()
            this.scrollToKycResult()
            window.location.reload();
          }
        })
        .catch(err => {
          this.logIdentificationIssue(
            'kyc/status',
            err,
            'Не удалось получить статус проверки. Попробуйте позже.'
          )
        })
    },

    restartKyc() {
      this.startOnboarding(true)
    },
    
    onKycSuccessContinue() {
      this.$router.push('/landing')
    },
    onBasicTestPassedClose() {
      this.basicTestResult = 'passed'
    },

    openInNewTab() {
      if (!this.kycFormUrl) return
      window.open(this.kycFormUrl, '_blank', 'noopener,noreferrer')
    },

    copyText(text) {
      if (!text) return
      navigator.clipboard
        .writeText(text)
        .then(() => {
          console.log('[Identification:copy]', 'Ссылка скопирована в буфер обмена.')
        })
        .catch(() => {
          console.warn('[Identification:copy]', 'Не удалось скопировать. Скопируйте вручную.')
        })
    },

    formatKey(key) {
      const map = {
        profile: 'Профиль',
        document: 'Документ',
        address: 'Адрес',
        database_screening: 'Проверка по базам'
      }
      return map[key] || String(key || '').replace(/_/g, ' ')
    },

    formatDate(dateStr) {
      if (!dateStr) return ''
      return new Date(dateStr).toLocaleString('ru-RU')
    },

    sendReply() {
      console.log('[Identification:reply]', 'Ваш ответ отправлен и будет рассмотрен.')
      this.replyText = ''
      this.replyFile = null
    }
  },

  created() {
    this.setOperationTypeFromRoute(this.$route.params.type)
    this.loadSurveyDefinition()
  },

  beforeDestroy() {
    this.stopPollingKyc()
  }
}
</script>
<style scoped>
.status-card {
  border-radius: 20px;
  padding: 24px;
  background: linear-gradient(135deg, rgba(30, 41, 59, 0.92), rgba(15, 23, 42, 0.95));
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(14px);
}

.status-block {
  text-align: center;
  border-radius: 16px;
  padding: 22px;
}

.status-block h4 {
  font-weight: 900;
  color: #f8fafc;
}

.status-block p {
  color: rgba(226, 232, 240, 0.85);
}

.info {
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.15), rgba(79, 70, 229, 0.18));
}

.pending {
  background: linear-gradient(135deg, rgba(251, 191, 36, 0.16), rgba(245, 158, 11, 0.18));
}

.approved {
  background: linear-gradient(135deg, rgba(34, 197, 94, 0.18), rgba(22, 163, 74, 0.22));
}

.rejected {
  background: linear-gradient(135deg, rgba(239, 68, 68, 0.18), rgba(220, 38, 38, 0.22));
}

.survey-block .survey-form {
  text-align: left;
}
.survey-label {
  display: block;
  color: rgba(255, 255, 255, 0.9);
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 6px;
}
.survey-select {
  background: rgba(0, 0, 0, 0.25);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  color: #f8fafc;
  padding: 8px 12px;
  width: 100%;
  max-width: 360px;
}
.survey-select:focus {
  border-color: rgba(34, 197, 94, 0.5);
  box-shadow: 0 0 0 2px rgba(34, 197, 94, 0.2);
}
.survey-error {
  color: #fca5a5;
  font-size: 14px;
  margin: 0 0 10px 0;
}

.pending-actions {
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.start-btn {
  font-weight: 900;
  padding: 10px 18px;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.25);
}

.text-muted-soft {
  color: rgba(226, 232, 240, 0.75);
}

.error-box {
  width: 100%;
  max-width: 780px;
  padding: 12px 14px;
  border-radius: 14px;
  color: rgba(254, 226, 226, 0.95);
  background: linear-gradient(135deg, rgba(239, 68, 68, 0.18), rgba(185, 28, 28, 0.22));
  border: 1px solid rgba(248, 113, 113, 0.25);
  text-align: left;
}


.iframe-shell {
  width: 100%;
  max-width: 980px;
  border-radius: 18px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.10);
  background: linear-gradient(135deg, rgba(2, 6, 23, 0.65), rgba(15, 23, 42, 0.55));
  box-shadow: 0 18px 45px rgba(0, 0, 0, 0.35);
}

.iframe-header {
  padding: 12px 14px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.iframe-title {
  font-weight: 900;
  color: #f8fafc;
  display: flex;
  align-items: center;
}

.iframe-meta {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 8px;
}

.meta-pill {
  padding: 6px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 900;
  color: rgba(226, 232, 240, 0.95);
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.10);
}

.iframe-wrap {
  width: 100%;
  height: 720px;
  background: rgba(0, 0, 0, 0.15);
  position: relative;
}

.iframe-top-hint {
  position: absolute;
  top: 10px;
  left: 10px;
  right: 10px;
  z-index: 2;
  display: flex;
  justify-content: flex-start;
  pointer-events: none;
}

.hint-pill {
  pointer-events: auto;
  padding: 8px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 800;
  color: rgba(226, 232, 240, 0.92);
  background: rgba(0, 0, 0, 0.35);
  border: 1px solid rgba(255, 255, 255, 0.10);
  backdrop-filter: blur(8px);
}

.hint-pill a {
  color: #fff;
  text-decoration: underline;
}

.iframe-wrap iframe {
  width: 100%;
  height: 100%;
  border: 0;
}

.iframe-footer {
  padding: 10px 14px;
  font-size: 12px;
  font-weight: 700;
  color: rgba(203, 213, 225, 0.9);
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}

.iframe-problem {
  padding: 18px 16px;
  text-align: left;
}

.problem-title {
  display: flex;
  align-items: center;
  font-weight: 900;
  color: #f8fafc;
  margin-bottom: 8px;
}

.problem-text {
  color: rgba(226, 232, 240, 0.85);
  font-weight: 700;
}

.problem-actions {
  margin-top: 12px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.help-list {
  margin-top: 12px;
  padding: 12px 14px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.10);
  color: rgba(226, 232, 240, 0.9);
  font-size: 13px;
  font-weight: 700;
}

.polling-pill {
  width: 100%;
  padding: 10px 12px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.10);
  color: rgba(226, 232, 240, 0.9);
  font-weight: 800;
  display: flex;
  align-items: center;
}

.details-box {
  border-radius: 18px;
  padding: 18px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.10);
}

.details-box h5 {
  color: #f8fafc;
  font-weight: 900;
  margin-bottom: 12px;
}

.detail-item {
  border-radius: 14px;
  padding: 12px 14px;
  background: rgba(0, 0, 0, 0.18);
  border: 1px solid rgba(255, 255, 255, 0.08);
  margin-bottom: 10px;
}

.detail-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  margin-bottom: 6px;
}

.detail-key {
  font-weight: 900;
  color: rgba(248, 250, 252, 0.95);
}

.detail-badge {
  padding: 6px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 900;
  border: 1px solid rgba(255, 255, 255, 0.10);
}

.detail-badge.ok {
  background: rgba(34, 197, 94, 0.18);
  color: rgba(220, 252, 231, 0.95);
}

.detail-badge.fail {
  background: rgba(239, 68, 68, 0.18);
  color: rgba(254, 226, 226, 0.95);
}

.comments-box {
  margin-top: 18px;
  border-radius: 20px;
  padding: 20px;
  background: linear-gradient(135deg, rgba(30, 41, 59, 0.88), rgba(15, 23, 42, 0.92));
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.comments-box h5 {
  color: #f8fafc;
  font-weight: 900;
}

.comment-item {
  border-left: 4px solid #6366f1;
  padding: 10px 14px;
  margin-bottom: 14px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.04);
}

.comment-header {
  font-size: 12px;
  font-weight: 800;
  color: rgba(203, 213, 225, 0.9);
  display: flex;
  gap: 12px;
}

.comment-body {
  margin-top: 6px;
  font-size: 14px;
  color: rgba(241, 245, 249, 0.9);
}

.reply-box {
  margin-top: 18px;
  border-radius: 20px;
  padding: 20px;
  background: linear-gradient(135deg, rgba(30, 41, 59, 0.88), rgba(15, 23, 42, 0.92));
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.reply-box h5 {
  color: #f8fafc;
  font-weight: 900;
}

.reply-box textarea,
.reply-box input {
  background: rgba(255, 255, 255, 0.06) !important;
  border: 1px solid rgba(255, 255, 255, 0.14) !important;
  color: #f8fafc !important;
}

.reply-box textarea::placeholder {
  color: rgba(203, 213, 225, 0.7);
}

.kyc-method-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 12px;
  margin-top: 12px;
}
.kyc-method-card {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  padding: 16px;
  background: rgba(255, 255, 255, 0.04);
  border: 2px solid rgba(255, 255, 255, 0.10);
  border-radius: 12px;
  color: rgba(241, 245, 249, 0.92);
  cursor: pointer;
  transition: 150ms ease;
}
.kyc-method-card:hover {
  border-color: rgba(239, 185, 20, 0.45);
  background: rgba(239, 185, 20, 0.06);
}
.kyc-method-card.is-selected {
  border-color: var(--Primary-1, #EFB914);
  background: rgba(239, 185, 20, 0.12);
}
.kyc-method-icon {
  font-size: 1.5rem;
  margin-bottom: 8px;
  color: var(--Primary-1, #EFB914);
}
.kyc-method-title {
  font-weight: 700;
  font-size: 16px;
  margin-bottom: 4px;
}
.kyc-method-sub {
  font-size: 13px;
  color: rgba(203, 213, 225, 0.72);
  line-height: 1.4;
}
</style>
